<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Modal from 'dashboard/components/Modal.vue';
import TemplateSequenceBuilder from 'dashboard/components-next/follow-ups/TemplateSequenceBuilder.vue';
import TemplatePreview from 'dashboard/components-next/follow-ups/TemplatePreview.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

// Route-based mode detection
const isEditMode = computed(() => !!route.params.templateId);
const templateId = ref(null);

// Form state
const templateName = ref('');
const templateActive = ref(true);
const isSaving = ref(false);
const isLoadingTemplate = ref(false);
const hasUnsavedChanges = ref(false);
// OBS-FU-03: legacy text-only templates have `content` but no sequence items.
const legacyContent = ref('');

// Save-time name modal
const showNameModal = ref(false);
const modalNameInput = ref('');
const modalInputRef = ref(null);

const availableVariables = [
  '{{contact_name}}',
  '{{contact_phone}}',
  '{{contact_email}}',
  '{{conversation_id}}',
  '{{inbox_name}}',
  '{{agent_name}}',
  '{{card_title}}',
  '{{card_value}}',
  '{{stage_name}}',
  '{{pipeline_name}}',
  '{{current_date}}',
  '{{current_time}}',
];

// Items from store (for preview)
const items = computed(() => {
  if (!templateId.value) return [];
  return store.getters['followUpTemplates/getItems'](templateId.value) || [];
});

// OBS-FU-03: a legacy template (single-message `content`, no items) opens the
// sequence builder empty. Surface its existing content so it is not silently
// invisible — the user can add items to convert it into a sequence.
const hasLegacyContent = computed(
  () => legacyContent.value.trim().length > 0 && items.value.length === 0
);

const goBack = () => {
  router.push(accountScopedRoute('follow_up_templates'));
};

// Load template in edit mode
const loadTemplate = async id => {
  isLoadingTemplate.value = true;
  try {
    const data = await store.dispatch('followUpTemplates/show', id);
    templateName.value = data.name;
    templateActive.value = data.active;
    templateId.value = data.id;
    legacyContent.value = data.content || '';
  } catch {
    useAlert(t('FOLLOW_UP_TEMPLATES.MESSAGES.LOAD_ERROR'));
    goBack();
  } finally {
    isLoadingTemplate.value = false;
  }
};

onMounted(async () => {
  if (isEditMode.value) {
    const id = Number(route.params.templateId);
    await loadTemplate(id);
  }
});

// Track unsaved changes
watch(templateName, () => {
  hasUnsavedChanges.value = true;
});

// Core save logic (called after name is confirmed).
// Audit 2026-04-25 (BUG-FU01): do NOT inject name into content anymore —
// the new builder uses items; legacy content is optional.
const doSave = async () => {
  isSaving.value = true;
  try {
    if (templateId.value) {
      await store.dispatch('followUpTemplates/update', {
        id: templateId.value,
        name: templateName.value.trim(),
        active: templateActive.value,
      });
      useAlert(t('FOLLOW_UP_TEMPLATES.MESSAGES.UPDATE_SUCCESS'));
    } else {
      const data = await store.dispatch('followUpTemplates/create', {
        name: templateName.value.trim(),
        active: templateActive.value,
      });
      templateId.value = data.id;
      router.replace(
        accountScopedRoute('follow_up_template_edit', {
          templateId: data.id,
        })
      );
      useAlert(t('FOLLOW_UP_TEMPLATES.MESSAGES.CREATE_SUCCESS'));
    }
    hasUnsavedChanges.value = false;
  } catch {
    useAlert(t('FOLLOW_UP_TEMPLATES.MESSAGES.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// Save button click — show modal if unnamed or still using auto-save placeholder
const saveTemplate = () => {
  const placeholder = t('FOLLOW_UP_BUILDER.HEADER.NAME_PLACEHOLDER');
  const isPlaceholder = templateName.value.trim() === placeholder;
  if (templateName.value.trim() && !isPlaceholder) {
    doSave();
  } else {
    modalNameInput.value = '';
    showNameModal.value = true;
    nextTick(() => {
      modalInputRef.value?.focus();
    });
  }
};

const handleModalSave = async () => {
  const trimmed = modalNameInput.value.trim();
  if (!trimmed) return;
  templateName.value = trimmed;
  showNameModal.value = false;
  await doSave();
};

const handleModalCancel = () => {
  showNameModal.value = false;
};

const onItemsChanged = () => {
  // Items are already updated in store, preview will react automatically
};

// Auto-save: create template with placeholder name so user can add items immediately
// without needing to name the template first. Name can be set later via the Save modal.
// BUG-FU01 (audit 2026-04-25): no longer stuffs name into content.
const handleAutoSave = async () => {
  if (templateId.value) return; // already saved
  isSaving.value = true;
  const baseName = t('FOLLOW_UP_BUILDER.HEADER.NAME_PLACEHOLDER');
  try {
    let data;
    try {
      data = await store.dispatch('followUpTemplates/create', {
        name: baseName,
        active: templateActive.value,
      });
    } catch {
      // BUG-FU-01 (audit 2026-06-15): the placeholder name is unique per account,
      // so a second unnamed draft collided with 422 and the item was silently not
      // created. Retry once with a unique suffix so the builder can still save and
      // attach the item; the user renames it via the Save modal anyway.
      data = await store.dispatch('followUpTemplates/create', {
        name: `${baseName} ${Date.now().toString(36)}`,
        active: templateActive.value,
      });
    }
    templateId.value = data.id;
    router.replace(
      accountScopedRoute('follow_up_template_edit', { templateId: data.id })
    );
  } catch {
    useAlert(t('FOLLOW_UP_TEMPLATES.MESSAGES.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// Unsaved changes warning
onBeforeRouteLeave((_to, _from, next) => {
  if (hasUnsavedChanges.value) {
    // eslint-disable-next-line no-alert
    const answer = window.confirm(t('FOLLOW_UP_BUILDER.UNSAVED_CHANGES'));
    if (!answer) {
      next(false);
      return;
    }
  }
  next();
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <header
      class="relative flex-shrink-0 border-b border-n-weak bg-n-background px-6 py-4"
    >
      <div class="flex items-center justify-between gap-4">
        <!-- Left: back + icon chip + title -->
        <div class="flex items-center gap-3 min-w-0">
          <button
            type="button"
            class="rounded-lg p-1.5 text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 flex-shrink-0"
            @click="goBack"
          >
            <span class="i-lucide-chevron-left size-5" aria-hidden="true" />
          </button>
          <div
            class="flex size-10 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand flex-shrink-0"
          >
            <span class="i-lucide-file-edit size-5" aria-hidden="true" />
          </div>
          <div class="min-w-0">
            <h1 class="text-lg font-semibold text-n-slate-12 truncate">
              {{
                templateName.trim() ||
                t('FOLLOW_UP_BUILDER.HEADER.NAME_PLACEHOLDER')
              }}
            </h1>
            <p class="text-xs text-n-slate-10">
              {{ t('FOLLOW_UP_BUILDER.HEADER.SUBTITLE') }}
            </p>
          </div>
        </div>

        <!-- Right: active toggle + save button -->
        <div class="flex items-center gap-3 flex-shrink-0">
          <!-- Active toggle — visual pill -->
          <button
            type="button"
            class="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors"
            :class="
              templateActive
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-400'
                : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="templateActive = !templateActive"
          >
            <span
              class="size-1.5 rounded-full"
              :class="templateActive ? 'bg-emerald-500' : 'bg-n-slate-9'"
            />
            {{ t('FOLLOW_UP_BUILDER.HEADER.ACTIVE') }}
          </button>

          <!-- Save button -->
          <button
            type="button"
            class="flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            :disabled="isSaving"
            @click="saveTemplate"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
              aria-hidden="true"
            />
            <span v-else class="i-lucide-save size-4" aria-hidden="true" />
            {{
              templateId
                ? t('FOLLOW_UP_BUILDER.HEADER.SAVE')
                : t('FOLLOW_UP_BUILDER.HEADER.CREATE_AND_BUILD')
            }}
          </button>
        </div>
      </div>
    </header>

    <!-- Loading state -->
    <div
      v-if="isLoadingTemplate"
      class="flex-1 flex items-center justify-center"
    >
      <div
        class="w-8 h-8 border-2 border-n-weak border-t-n-brand rounded-full animate-spin"
      />
    </div>

    <!-- Main Content - Split Layout -->
    <div v-else class="flex-1 flex overflow-hidden">
      <!-- Left Panel: Sequence Builder -->
      <div class="w-3/5 border-r border-n-weak overflow-y-auto p-4">
        <!-- OBS-FU-03: legacy single-message template (content, no items) -->
        <div
          v-if="hasLegacyContent"
          class="mb-4 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
        >
          <div class="flex items-center gap-1.5 mb-1.5">
            <span class="i-lucide-info size-3.5 text-n-slate-11" />
            <p class="text-xs font-medium text-n-slate-12">
              {{ t('FOLLOW_UP_BUILDER.LEGACY.TITLE') }}
            </p>
          </div>
          <p class="text-[11px] text-n-slate-11 mb-2">
            {{ t('FOLLOW_UP_BUILDER.LEGACY.DESCRIPTION') }}
          </p>
          <p
            class="text-[11px] text-n-slate-12 whitespace-pre-wrap rounded bg-n-solid-2 p-2 border border-n-weak"
          >
            {{ legacyContent }}
          </p>
        </div>

        <TemplateSequenceBuilder
          :template-id="templateId"
          :available-variables="availableVariables"
          @items-changed="onItemsChanged"
          @request-auto-save="handleAutoSave"
        />
      </div>

      <!-- Right Panel: Preview -->
      <div class="w-2/5 overflow-hidden p-4 bg-n-solid-2">
        <div class="mb-3 flex items-center gap-2">
          <div
            class="flex size-7 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand flex-shrink-0"
          >
            <span class="i-lucide-smartphone size-4" aria-hidden="true" />
          </div>
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('FOLLOW_UP_BUILDER.PREVIEW.TITLE') }}
            </h3>
            <p class="text-[10px] text-n-slate-10">
              {{ t('FOLLOW_UP_BUILDER.PREVIEW.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="h-[calc(100%-3rem)]">
          <TemplatePreview :items="items" :template-name="templateName" />
        </div>
      </div>
    </div>
  </div>

  <!-- Name prompt modal — shown when saving without a name -->
  <Modal v-model:show="showNameModal" :on-close="handleModalCancel">
    <div class="px-8 py-7 w-full">
      <!-- Icon + heading -->
      <div class="flex items-start gap-4 mb-6">
        <div
          class="flex-shrink-0 w-10 h-10 rounded-xl bg-n-brand/10 flex items-center justify-center"
        >
          <span class="i-lucide-bookmark size-5 text-n-brand" />
        </div>
        <div>
          <h3 class="text-base font-semibold text-n-slate-12 leading-snug">
            {{ t('FOLLOW_UP_BUILDER.SAVE_MODAL.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10 mt-0.5">
            {{ t('FOLLOW_UP_BUILDER.SAVE_MODAL.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Input -->
      <label
        for="tpl-save-name"
        class="block text-xs font-medium text-n-slate-11 mb-1.5 uppercase tracking-wide"
      >
        {{ t('FOLLOW_UP_BUILDER.SAVE_MODAL.NAME_LABEL') }}
      </label>
      <input
        id="tpl-save-name"
        ref="modalInputRef"
        v-model="modalNameInput"
        name="template_name"
        type="text"
        class="w-full px-3 py-2.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand/50 transition-shadow"
        :placeholder="t('FOLLOW_UP_BUILDER.SAVE_MODAL.NAME_PLACEHOLDER')"
        @keydown.enter="handleModalSave"
      />

      <!-- Actions -->
      <div class="flex gap-2 mt-6">
        <button
          class="flex-1 py-2 text-sm font-medium rounded-lg bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
          @click="handleModalCancel"
        >
          {{ t('FOLLOW_UP_BUILDER.SAVE_MODAL.CANCEL') }}
        </button>
        <button
          class="flex-1 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="!modalNameInput.trim()"
          @click="handleModalSave"
        >
          {{ t('FOLLOW_UP_BUILDER.SAVE_MODAL.SAVE') }}
        </button>
      </div>
    </div>
  </Modal>
</template>
